// React
import { ReactElement, useEffect, useState } from 'react';
// Next
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
// Design System
import { Font } from '@utmb/design-system/text/font';
import { Button } from '@utmb/design-system/button/Button';
// Features
import { RaceTimeCharts } from '@utmb/features/race-track/time-chart/RaceTimeCharts';
import { RaceProfileWrapper } from '@utmb/features/race-track/profile/RaceProfileWrapper';
import { RaceProfileDownload, RaceGpxDownload } from '@utmb/features/race-track/profile/RaceProfileDownload';
// Types
import { Slice } from '@utmb/types/slice-handler';
import { SliceRaceProfileMapTableView } from '@utmb/types/index';
// Context, utils
import { useRaceTrack } from '@utmb/contexts/RaceTrackContext';
// Styles
import styles from './race-track.module.scss';
import { RaceProfile3D } from './profile3d/RaceProfile3D';

const raceMapPlaceholder = (): ReactElement => <div className={styles.map_loading_placeholder} />;

// Dynamic Map import
const RaceMap = dynamic(() => import('src/features/race-track/map/RaceMap').then((comp) => comp.RaceMap as any), {
    ssr: false,
    loading: raceMapPlaceholder,
});

const tabs = [{ id: 'profile' }, { id: 'map' }, { id: 'timeCharts' }, { id: 'profile3d' }];

// Main Component
// noinspection JSUnusedGlobalSymbols
export const RaceTrack = (props: Slice<SliceRaceProfileMapTableView>): ReactElement => {
    const { id, hideGpxDownload, hideProfile, hideMap, hideTable, hide3D, hideSlowest } = props;
    const { track, color, url3d, gpxUrl } = useRaceTrack();

    const { t } = useTranslation();

    const [activeTabId, setActiveTabId] = useState(tabs[0].id);

    useEffect(() => {
        if (hideProfile) setActiveTabId(!hideMap ? tabs[1].id : !hideTable ? tabs[2].id : tabs[3].id);
    }, [hideProfile, hideMap, hideTable]);

    if (hideProfile && hideMap && hideTable && (hide3D || !url3d)) return <></>;

    const navigationCSS = [styles.navigation].filter(Boolean).join(' ');

    return track && track.points?.length > 0 && color ? (
        <section id={id} className={styles.section}>
            <div className={styles.tabs_container}>
                {activeTabId === tabs[0].id && !hideProfile && <RaceProfileWrapper />}
                {activeTabId === tabs[1].id && !hideMap && <RaceMap />}
                {activeTabId === tabs[2].id && !hideTable && (
                    <div className={styles.timeCharts}>
                        <RaceTimeCharts hideSlowest={hideSlowest} />
                    </div>
                )}
                {url3d && activeTabId === tabs[3].id && !hide3D && <RaceProfile3D />}
            </div>
            <div className={styles.navigation_container}>
                <div className={navigationCSS}>
                    <div>
                        {activeTabId === tabs[0].id && <RaceProfileDownload />}
                        {activeTabId === tabs[1].id && !hideGpxDownload && gpxUrl && <RaceGpxDownload />}
                    </div>
                    <nav>
                        <ul>
                            {tabs
                                .filter((value) => {
                                    if (value.id === tabs[0].id) return !hideProfile && value;
                                    if (value.id === tabs[1].id) return !hideMap && value;
                                    if (value.id === tabs[2].id) return !hideTable && value;
                                    if (value.id === tabs[3].id && url3d) return !hide3D && value;
                                    return false;
                                })
                                .map((tab, idx) => (
                                    <li key={tab.id} className={`${activeTabId === tab.id && styles.link_selected}`}>
                                        <Button.Ghost
                                            className={`${styles.tab_btn_wrapper} ${activeTabId === tab.id && styles.link}`}
                                            onPress={() => setActiveTabId(tabs[idx].id)}
                                        >
                                            <Font.FuturaHeavy mobile="16">{t(`raceTrack.menu.${tab.id}`)}</Font.FuturaHeavy>
                                        </Button.Ghost>
                                    </li>
                                ))}
                        </ul>
                    </nav>
                </div>
            </div>
        </section>
    ) : (
        <></>
    );
};
